/**
 * Bornes de journee dans le fuseau du SALON.
 *
 * Tentation a eviter : se fier au fuseau du navigateur. Il coincide presque
 * toujours avec celui du salon... sauf quand la gerante consulte le planning
 * depuis l'etranger, ou la nuit du changement d'heure. Le planning affiche
 * l'heure du salon, point.
 */

function offsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);

  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
  return asUtc - instant.getTime();
}

/** `2026-09-01` + fuseau -> instant absolu de minuit local. */
export function zonedStartOfDay(dateKey: string, timeZone: string): Date {
  const naive = new Date(`${dateKey}T00:00:00Z`).getTime();
  // Deux passes : la premiere estime le decalage, la seconde le corrige
  // pour les jours de bascule heure d'ete / heure d'hiver.
  const first = new Date(naive - offsetMs(new Date(naive), timeZone));
  return new Date(naive - offsetMs(first, timeZone));
}

export function zonedEndOfDay(dateKey: string, timeZone: string): Date {
  const start = zonedStartOfDay(dateKey, timeZone);
  return new Date(start.getTime() + 86_400_000);
}

/**
 * Cle calendaire d'un instant, dans le fuseau du salon.
 *
 * `en-CA` produit `2026-08-29` : c'est le seul reglage de `Intl` qui donne
 * l'ordre ISO sans avoir a recomposer la chaine soi-meme.
 */
export function dayKeyOf(instant: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(instant);
}

/** Cle calendaire du jour courant, dans le fuseau du salon. */
export function todayKey(timeZone: string): string {
  return dayKeyOf(new Date(), timeZone);
}

export function shiftDayKey(dateKey: string, days: number): string {
  const shifted = new Date(`${dateKey}T12:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
}

/** Minutes ecoulees depuis minuit local — position verticale dans la grille. */
export function minutesFromDayStart(instant: Date, dayStart: Date): number {
  return (instant.getTime() - dayStart.getTime()) / 60_000;
}
